import {cloneDeep, countBy, filter, find, first, isEmpty, isEqual, map, some, xor} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as uuid from 'uuid/v4';

import {groupApi, settingsApi, userApi} from 'api';
import {
    ActionButton, AssignmentSelectors, Button, Card, ConfirmModal,
    DataWrapper, PredefinedNotifierType, Tree, TreeNode, TreeRenderProps, TreeValidateProps, UnsavedChangesLeavePrompt
} from 'components';
import {useApiData, useTree} from 'hooks';
import {AsyncRequestTypeName, Group, Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InteractiveChatAction, InteractiveChatItem, NodePath, UserSummary} from 'types';
import {interactiveChatUtils} from 'utils/interactive-chat';
import {buildTranslationKey} from 'utils/transform';

import {InteractiveChatNode} from './InteractiveChatNode';
import {LLM_CHATBOT_ACTIONS, NODE_CHILD_ACTIONS, ROOT_NODES_ACTIONS} from './constants';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.INTERACTIVE_CHAT;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type TreeRootNodeProps = {
    rootNodeId: string;
    users: UserSummary[];
    groups: Group[];
    interactiveChatOptions: Option[];
    asyncRequestOptions: Option[];
    selectedGroupId: string;
    initialValue: TreeNode<InteractiveChatItem>;
    onChange: (rootNode: TreeNode<InteractiveChatItem>, rootNodeId: string) => void;
    onInvalidChange: (isInvalid: boolean) => void;
}

export const TreeRootNode: React.FC<TreeRootNodeProps> = ({
    initialValue, selectedGroupId, users, groups, interactiveChatOptions, asyncRequestOptions, rootNodeId, onChange, onInvalidChange
}) => {
    const {t} = useTranslation();
    const {
        tree, mergeNodeValue, nodeState, isInvalid,
        getNode, updateNodeState, addNode, removeNode, removeChildNodes
    } = useTree<InteractiveChatItem>(initialValue);
    const [confirmRemoveModalProps, setConfirmRemoveModalProps] = useState<{open: boolean, path?: NodePath}>({
        open: false
    });
    const [confirmUpdateModalProps, setConfirmUpdateModalProps] = useState<{open: boolean, value?: InteractiveChatItem, path?: NodePath}>({
        open: false
    });
    const canSelectAgent = useMemo(() => !selectedGroupId, [selectedGroupId]);
    const getNodeAction = useCallback((path: NodePath): InteractiveChatAction => {
        const parentNode = getNode(path);
        return parentNode?.value.action;
    }, [getNode]);

    const validateNode = useCallback(({value, children, level, isEdit}: TreeValidateProps<InteractiveChatItem>): boolean => {
        if (!value.text) {
            return false;
        }

        if (level === 0 && !ROOT_NODES_ACTIONS.includes(value.action)) {
            return false;
        }

        if (level < 2 && value.action === InteractiveChatAction.Back) {
            return false;
        }

        if (value.action === InteractiveChatAction.Tree && !isEdit) {
            if (isEmpty(children)) {
                return false;
            }
            const hasAsyncRequestChild = find(children, (child) => child.value.action === InteractiveChatAction.AsyncRequest);
            if (hasAsyncRequestChild) {
                return children.length > 1;
            }
            return true;
        }

        if (LLM_CHATBOT_ACTIONS.includes(value.action)) {
            const childActionsCount = countBy(children, (child) => child.value.action);
            const callAgentChildrenCount = childActionsCount[InteractiveChatAction.CallAgent];
            const requestAppointmentChildrenCount = childActionsCount[InteractiveChatAction.RequestAppointment];
            const asyncRequestChildrenCount = childActionsCount[InteractiveChatAction.AsyncRequest];

            if (callAgentChildrenCount > 1 || requestAppointmentChildrenCount > 1 || asyncRequestChildrenCount > 1) {
                return false;
            }

            const hasInvalidAsyncRequestChild = some(children, (child) =>
                child.value.action === InteractiveChatAction.AsyncRequest && !child.value.actionData?.asyncRequest?.condition);
            if (hasInvalidAsyncRequestChild) {
                return false;
            }
        }

        if (value.action === InteractiveChatAction.AsyncRequest) {
            if (!value.actionData?.asyncRequest?.typeName) {
                return false;
            }
            const childActionsCount = countBy(children, (child) => child.value.action);
            const callAgentChildrenCount = childActionsCount[InteractiveChatAction.CallAgent];
            const requestAppointmentChildrenCount = childActionsCount[InteractiveChatAction.RequestAppointment];
            const treeChildrenCount = childActionsCount[InteractiveChatAction.Tree];
            if (callAgentChildrenCount > 1 || requestAppointmentChildrenCount > 1 || treeChildrenCount > 1) {
                return false;
            }
        }

        if (value.action === InteractiveChatAction.Watsonx) {
            const childActionsCount = countBy(children, (child) => child.value.action);
            const callAgentChildrenCount = childActionsCount[InteractiveChatAction.CallAgent];
            if (callAgentChildrenCount > 1) {
                return false;
            }
        }

        if (value.action === InteractiveChatAction.InternalLiveAgent) {
            const childActionsCount = countBy(children, (child) => child.value.action);
            const callAgentChildrenCount = childActionsCount[InteractiveChatAction.CallAgent];
            if (callAgentChildrenCount > 1) {
                return false;
            }
        }

        if (value.action === InteractiveChatAction.InteractiveChat) {
            if (!value.actionData?.interactiveChatId) {
                return false;
            }

            const hasValidInteractiveChat = some(interactiveChatOptions, {id: value.actionData.interactiveChatId});
            if (!hasValidInteractiveChat) {
                return false;
            }
        }

        return true;
    }, [interactiveChatOptions]);

    const renderNode = useCallback((props: TreeRenderProps<InteractiveChatItem>) => (
        <InteractiveChatNode
            {...props}
            users={users}
            groups={groups}
            getNodeAction={getNodeAction}
            interactiveChatOptions={interactiveChatOptions}
            asyncRequestOptions={asyncRequestOptions}
            canSelectAgent={canSelectAgent}
        />
    ),
    [users, groups, getNodeAction, canSelectAgent, interactiveChatOptions, asyncRequestOptions]);

    const canAddChild = useCallback((node: TreeNode<InteractiveChatItem>) => {
        if (node.value.action === InteractiveChatAction.AsyncRequest) {
            const hasCallAgent = find(node.children, (child) => child.value.action === InteractiveChatAction.CallAgent);
            const hasRequestAppointment = find(node.children, (child) => child.value.action === InteractiveChatAction.RequestAppointment);
            const hasTree = find(node.children, (child) => child.value.action === InteractiveChatAction.Tree);
            return !(hasCallAgent && hasRequestAppointment && hasTree);
        }

        if (!ROOT_NODES_ACTIONS.includes(node.value.action)) {
            return false;
        }

        if (LLM_CHATBOT_ACTIONS.includes(node.value.action)) {
            const hasCallAgent = find(node.children, (child) => child.value.action === InteractiveChatAction.CallAgent);
            const hasRequestAppointment = find(node.children, (child) => child.value.action === InteractiveChatAction.RequestAppointment);
            const hasAsyncRequest = find(node.children, (child) => child.value.action === InteractiveChatAction.AsyncRequest);

            if (hasCallAgent && hasRequestAppointment && hasAsyncRequest) {
                return false;
            }
        }

        if (node.value.action === InteractiveChatAction.Watsonx) {
            const hasCallAgent = find(node.children, (child) => child.value.action === InteractiveChatAction.CallAgent);
            return !hasCallAgent;
        }

        if (node.value.action === InteractiveChatAction.InternalLiveAgent) {
            const hasCallAgent = find(node.children, (child) => child.value.action === InteractiveChatAction.CallAgent);
            return !hasCallAgent;
        }

        return true;
    }, []);

    const handleAddChild = useCallback((path: NodePath) => {
        const nodeAction = getNodeAction(path);
        let node = {text: t(T_KEYS.MESSAGE), action: InteractiveChatAction.SendMessage};

        if (LLM_CHATBOT_ACTIONS.includes(nodeAction)) {
            const chatbotActions = NODE_CHILD_ACTIONS[nodeAction] as InteractiveChatAction[];
            let action = first(chatbotActions);
            const parent = getNode(path);
            const existingActions = map(parent?.children, (child) => child.value.action);
            const availableActions = xor(chatbotActions, existingActions);
            if (!isEmpty(availableActions)) {
                action = first(availableActions);
            }
            node = {text: t(T_KEYS.ACTIONS[buildTranslationKey(action)]), action};
        }

        if (nodeAction === InteractiveChatAction.AsyncRequest) {
            const asyncRequestActions = NODE_CHILD_ACTIONS[InteractiveChatAction.AsyncRequest];
            let action = first(asyncRequestActions);
            const parent = getNode(path);
            const existingActions = map(parent?.children, (child) => child.value.action);
            const availableActions = xor(asyncRequestActions, existingActions);
            if (!isEmpty(availableActions)) {
                action = first(availableActions);
            }
            node = {text: t(T_KEYS.ACTIONS[buildTranslationKey(action)]), action};
        }

        if (nodeAction === InteractiveChatAction.Watsonx) {
            node = {text: t(T_KEYS.ACTIONS.CALL_AGENT), action: InteractiveChatAction.CallAgent};
        }

        if (nodeAction === InteractiveChatAction.InternalLiveAgent) {
            node = {text: t(T_KEYS.ACTIONS.CALL_AGENT), action: InteractiveChatAction.CallAgent};
        }

        addNode(path, node);
        updateNodeState(path, {expanded: true});
    }, [addNode, updateNodeState, getNode, getNodeAction, t]);

    const handleRemove = useCallback((path: NodePath) => {
        setConfirmRemoveModalProps({open: true, path});
    }, []);

    const updateNodeValue = useCallback((value: InteractiveChatItem, path: NodePath) => {
        const node = getNode(path);
        if (node?.value.action === InteractiveChatAction.Tree && !isEmpty(node.children) && value.action !== InteractiveChatAction.Tree) {
            setConfirmUpdateModalProps({open: true, path, value});
        } else {
            mergeNodeValue(path, value);
        }
    }, [getNode, mergeNodeValue]);

    const handleConfirmRemoveModalAction = useCallback((confirm: boolean) => {
        if (confirm && confirmRemoveModalProps.path) {
            removeNode(confirmRemoveModalProps.path);
        }
        setConfirmRemoveModalProps({open: false});
    }, [confirmRemoveModalProps, removeNode]);

    const handleConfirmUpdateModalAction = useCallback((confirm: boolean) => {
        if (confirm && confirmUpdateModalProps.path) {
            removeChildNodes(confirmUpdateModalProps.path);
            mergeNodeValue(confirmUpdateModalProps.path, confirmUpdateModalProps.value);
        }
        setConfirmUpdateModalProps({open: false});
    }, [confirmUpdateModalProps, mergeNodeValue, removeChildNodes]);

    useEffect(() => {
        onChange(tree, rootNodeId);
    }, [tree, rootNodeId, onChange]);

    useEffect(() => {
        onInvalidChange(isInvalid);
    }, [isInvalid, onInvalidChange]);

    return tree ? (
        <Fragment>
            <Tree
                editable
                removable
                node={tree}
                render={renderNode}
                validate={validateNode}
                canAddChild={canAddChild}
                nodeState={nodeState}
                updateNodeState={updateNodeState}
                onAdd={handleAddChild}
                onRemove={handleRemove}
                onNodeValueChange={updateNodeValue}
            />
            <ConfirmModal
                {...confirmRemoveModalProps}
                title={t(T_KEYS.CONFIRM_REMOVE_MODAL.TITLE)}
                onClose={handleConfirmRemoveModalAction}
            />
            <ConfirmModal
                {...confirmUpdateModalProps}
                title={t(T_KEYS.CONFIRM_REMOVE_CHILD_MODAL.TITLE)}
                onClose={handleConfirmUpdateModalAction}
            />
        </Fragment>
    ) : null;
};

export const InteractiveChat: React.FC = () => {
    const {t} = useTranslation();
    const [selectedGroupId, setSelectedGroupId] = useState<string>(null);
    const [rootNodes, setRootNodes] = useState<TreeNode<InteractiveChatItem>[]>([]);
    const [invalidTreeMap, setInvalidTreeMap] = useState<Record<string, boolean>>({});
    const hasInvalidTrees = useMemo(() => {
        for (const id in invalidTreeMap) {
            if (invalidTreeMap[id]) {
                return true;
            }
        }
        return false;
    }, [invalidTreeMap]);
    const onGetSuccess = useCallback((nodes: TreeNode<InteractiveChatItem>[]) => {
        setRootNodes(cloneDeep(nodes));
    }, []);
    const {send: getInteractiveChat, data, loading: getLoading} = useApiData(settingsApi.getInteractiveChat, {
        onSuccess: onGetSuccess,
        loadingDefault: true
    });

    const getInteractiveChatFn = useCallback(() => {
        getInteractiveChat(selectedGroupId);
    }, [getInteractiveChat, selectedGroupId]);

    const {
        send: updateInteractiveChat,
        loading: updateLoading
    } = useApiData(settingsApi.updateInteractiveChat, {
        onSuccess: getInteractiveChatFn,
        notificationType: PredefinedNotifierType.Save
    });

    const {send: getUsers, data: users} = useApiData(userApi.getUsersSummary);
    const {send: getGroups, data: groups} = useApiData(groupApi.getGroups);

    const interactiveChatOptions = useMemo(() =>
        map(rootNodes, interactiveChatUtils.buildInteractiveChatOption), [rootNodes]);

    const asyncRequestOptions = useMemo(() => map([AsyncRequestTypeName.SumaDocumentVerificationSDK], (typeName) => ({
        id: typeName,
        name: t(TRANSLATION_KEYS.COMMON.SESSION.ASYNC_REQUEST.TYPE[buildTranslationKey(typeName)])
    })), [t]);

    const hasChanges = useMemo(() => !isEqual(data, rootNodes), [data, rootNodes]);

    const handleUpdate = useCallback(() => {
        updateInteractiveChat(selectedGroupId, rootNodes);
    }, [rootNodes, selectedGroupId, updateInteractiveChat]);

    const handleAddRoot = useCallback(() => {
        const action = InteractiveChatAction.Tree;
        const nameTranslationKey = buildTranslationKey(action);
        setRootNodes((prev) => {
            const text = [t(T_KEYS.DEFAULT_ROOT_NODE_TEXT), prev.length + 1].join(' ');
            const item = {
                id: uuid(),
                value: {
                    text,
                    name: t(T_KEYS.ACTIONS[nameTranslationKey]),
                    action: InteractiveChatAction.Tree
                },
                children: [{
                    id: uuid(),
                    value: {
                        text: t(T_KEYS.MESSAGE),
                        action: InteractiveChatAction.SendMessage
                    },
                    children: []
                }]
            } as TreeNode<InteractiveChatItem>;
            return [...prev, item];
        });
    }, [t]);

    const handleRootChange = useCallback((rootNode: TreeNode<InteractiveChatItem>, rootNodeId: string) => {
        setRootNodes((prev) => {
            if (!rootNode) {
                return filter(prev, (root) => root.id !== rootNodeId);
            }

            return map(prev, (root) => root.id === rootNodeId ? rootNode : root);
        });
    }, []);

    const handleInvalidChange = useCallback((id: string, isInvalid: boolean) => {
        setInvalidTreeMap((prev) => {
            if (prev?.[id] !== isInvalid) {
                return {
                    ...prev,
                    [id]: isInvalid
                };
            } else {
                return prev;
            }
        });
    }, []);

    useEffect(() => {
        getInteractiveChatFn();
    }, [getInteractiveChatFn]);

    useEffect(() => {
        getUsers({activated: true, groupId: selectedGroupId});
    }, [selectedGroupId, getUsers]);

    useEffect(() => {
        getGroups();
    }, [getGroups]);

    return (
        <div>
            <div className='tw-text-default-dimmed tw-mb-4'>{t(T_KEYS.DESCRIPTION)}</div>
            <div className='sm:tw-flex tw-justify-between tw-items-center tw-mb-2'>
                <AssignmentSelectors
                    groups={groups}
                    selectedGroupId={selectedGroupId}
                    onChange={setSelectedGroupId}
                >
                    {({renderGroup}) => (
                        <div className='tw-mb-4 tw-inline-block tw-w-full sm:tw-w-64'>
                            {renderGroup()}
                        </div>
                    )}
                </AssignmentSelectors>
                <div className='tw-flex tw-items-center'>
                    <ActionButton
                        action='create'
                        className='tw-mr-2'
                        loading={getLoading || updateLoading}
                        onClick={handleAddRoot}
                    >
                        {t(T_KEYS.ADD_TEMPLATE)}
                    </ActionButton>
                    <Button
                        color='primary'
                        width='small'
                        disabled={!hasChanges || hasInvalidTrees}
                        loading={getLoading || updateLoading}
                        onClick={handleUpdate}
                    >
                        {t(T_KEYS_COMMON.BUTTONS.SAVE)}
                    </Button>
                </div>
            </div>
            <Card className='tw-relative tw-min-h-16'>
                <DataWrapper isLoading={getLoading || updateLoading} hideContentOnLoading={false} noData={isEmpty(rootNodes)}>
                    {map(rootNodes, (rootNode) => (
                        <TreeRootNode
                            key={rootNode.id}
                            selectedGroupId={selectedGroupId}
                            users={users}
                            groups={groups}
                            interactiveChatOptions={interactiveChatOptions}
                            asyncRequestOptions={asyncRequestOptions}
                            rootNodeId={rootNode.id}
                            initialValue={rootNode}
                            onChange={handleRootChange}
                            onInvalidChange={(isInvalid: boolean) => handleInvalidChange(rootNode.id, isInvalid)}
                        />
                    ))}
                </DataWrapper>
            </Card>
            <UnsavedChangesLeavePrompt values={rootNodes} initialValues={data} />
        </div>
    );
};